import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, RotateCcw, Loader2, Sparkles, Check, AlertCircle,
  Target, ListChecks, ShieldCheck, FileText, Lock,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PACKS } from "@/data/content";
import { submitDiagnostic, getDiagnosticQuestions } from "@/lib/api";

const STATES = { INTRO: "intro", FORM: "form", CAPTURE: "capture", LOADING: "loading", RESULT: "result" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PRIORITY_STYLES = {
  faible: { label: "Priorité faible", cls: "bg-green-100 text-green-700" },
  moyen: { label: "Priorité moyenne", cls: "bg-amber-100 text-amber-700" },
  "élevé": { label: "Priorité élevée", cls: "bg-orange-100 text-orange-700" },
  urgent: { label: "Priorité urgente", cls: "bg-red-100 text-red-700" },
};

const OptionButton = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left rounded-xl border-2 px-5 py-4 font-medium transition-colors ${
      selected
        ? "border-green-600 bg-green-50 text-slate-900"
        : "border-slate-200 hover:border-slate-300 text-slate-700"
    }`}
  >
    <span className="flex items-center justify-between gap-3">
      {children}
      {selected && <Check size={18} className="text-green-600 shrink-0" />}
    </span>
  </button>
);

const QuestionField = ({ q, value, onChange }) => {
  if (q.type === "text") {
    return (
      <Input
        data-testid={`field-${q.id}`}
        value={value || ""}
        onChange={(e) => onChange(q.id, e.target.value)}
        placeholder={q.placeholder || ""}
      />
    );
  }
  if (q.type === "select") {
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(q.id, v)}>
        <SelectTrigger data-testid={`field-${q.id}`}>
          <SelectValue placeholder="Sélectionnez..." />
        </SelectTrigger>
        <SelectContent>
          {q.options.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`opt-${q.id}-${o.value}`}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (q.type === "textarea") {
    return (
      <Textarea
        data-testid={`field-${q.id}`}
        rows={3}
        value={value || ""}
        onChange={(e) => onChange(q.id, e.target.value)}
        placeholder={q.placeholder || ""}
      />
    );
  }
  // single / scale
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {q.options.map((o) => (
        <OptionButton
          key={o.value}
          selected={value === o.value}
          onClick={() => onChange(q.id, o.value)}
          testId={`opt-${q.id}-${o.value}`}
        >
          {o.label}
        </OptionButton>
      ))}
    </div>
  );
};

export const DiagnosticWizard = () => {
  const navigate = useNavigate();
  const [state, setState] = useState(STATES.INTRO);
  const [blocks, setBlocks] = useState([]);
  const [blockIndex, setBlockIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loadingQ, setLoadingQ] = useState(true);
  const [lead, setLead] = useState({ email: "", commerce: "" });

  useEffect(() => {
    (async () => {
      try {
        const data = await getDiagnosticQuestions();
        setBlocks(data.blocks || []);
      } catch {
        setError("Impossible de charger le diagnostic. Réessayez plus tard.");
      } finally {
        setLoadingQ(false);
      }
    })();
  }, []);

  const totalBlocks = blocks.length;
  const progress =
    state === STATES.RESULT ? 100
    : state === STATES.CAPTURE ? 100
    : state === STATES.FORM ? (blockIndex / totalBlocks) * 100
    : 0;

  const start = () => {
    setState(STATES.FORM);
    setBlockIndex(0);
    setAnswers({});
    setResult(null);
    setLead({ email: "", commerce: "" });
    setError("");
  };

  const onChange = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

  const currentBlock = blocks[blockIndex];

  const isBlockValid = () => {
    if (!currentBlock) return false;
    return currentBlock.questions.every((q) => q.optional || (answers[q.id] && `${answers[q.id]}`.length > 0));
  };

  const next = async () => {
    if (!isBlockValid()) {
      setError("Merci de répondre aux questions avant de continuer.");
      return;
    }
    setError("");
    if (blockIndex + 1 < totalBlocks) {
      setBlockIndex(blockIndex + 1);
    } else {
      setLead((l) => ({ ...l, commerce: l.commerce || answers.a_nom || "" }));
      setState(STATES.CAPTURE);
    }
  };

  const back = () => {
    setError("");
    if (blockIndex > 0) setBlockIndex(blockIndex - 1);
    else setState(STATES.INTRO);
  };

  const submitLead = async () => {
    if (!EMAIL_RE.test(lead.email.trim())) {
      setError("Merci d'indiquer une adresse email valide pour recevoir votre rapport.");
      return;
    }
    setError("");
    await finish();
  };

  const finish = async () => {
    setState(STATES.LOADING);
    setError("");
    const payloadAnswers = {
      ...answers,
      a_email: lead.email.trim(),
      a_nom: (lead.commerce || answers.a_nom || "").trim(),
    };
    try {
      const data = await submitDiagnostic({ answers: payloadAnswers });
      setResult(data);
      setState(STATES.RESULT);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
      setState(STATES.CAPTURE);
    }
  };

  const recommendedPack = result ? PACKS.find((p) => p.id === result.pack) : null;
  const priority = result ? PRIORITY_STYLES[result.priorite] || PRIORITY_STYLES.moyen : null;

  return (
    <div data-testid="diagnostic-wizard" className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 sm:p-10 max-w-2xl mx-auto">
      {(state === STATES.FORM || state === STATES.CAPTURE || state === STATES.RESULT) && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2 text-sm font-semibold text-slate-500">
            <span>
              {state === STATES.RESULT
                ? "Diagnostic terminé"
                : state === STATES.CAPTURE
                ? "Dernière étape — votre rapport"
                : `Étape ${blockIndex + 1} / ${totalBlocks} — ${currentBlock?.title}`}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2 [&>div]:bg-green-600" data-testid="diagnostic-progress" />
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* INTRO */}
        {state === STATES.INTRO && (
          <motion.div key="intro" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="text-center py-4">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-green-50 text-green-600 mb-6">
              <Sparkles size={30} />
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Diagnostic de visibilité locale</h3>
            <p className="text-slate-600 text-base sm:text-lg mb-6 max-w-md mx-auto">
              Une analyse en 6 étapes de votre présence locale : Google, réseaux sociaux, site, supports et communication. Vous obtenez un score, votre potentiel et un plan d'action clair.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {["Fiche Google", "Réseaux", "Site web", "Supports", "Communication"].map((t) => (
                <span key={t} className="text-xs font-semibold text-slate-600 bg-slate-100 rounded-full px-3 py-1">{t}</span>
              ))}
            </div>
            {error && <p className="text-red-600 text-sm mb-4" data-testid="diagnostic-error">{error}</p>}
            <button
              data-testid="diagnostic-start-button"
              onClick={start}
              disabled={loadingQ || !!error}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-xl px-7 py-3.5 font-semibold text-lg transition-colors shadow-sm"
            >
              {loadingQ ? <Loader2 size={20} className="animate-spin" /> : <>Commencer le diagnostic <ArrowRight size={20} /></>}
            </button>
            <p className="text-sm text-slate-400 mt-4">Gratuit, sans engagement — environ 3 minutes.</p>
          </motion.div>
        )}

        {/* FORM */}
        {state === STATES.FORM && currentBlock && (
          <motion.div key={`block-${blockIndex}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}>
            <p className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-1">{currentBlock.title}</p>
            {currentBlock.subtitle && <p className="text-slate-500 mb-7">{currentBlock.subtitle}</p>}

            <div className="space-y-7">
              {currentBlock.questions.map((q) => (
                <div key={q.id} data-testid={`question-${q.id}`}>
                  <Label className="block text-base font-semibold text-slate-900 mb-3">
                    {q.label}{!q.optional && <span className="text-green-600"> *</span>}
                  </Label>
                  <QuestionField q={q} value={answers[q.id]} onChange={onChange} />
                </div>
              ))}
            </div>

            {error && <p className="text-red-600 text-sm mt-5" data-testid="diagnostic-error">{error}</p>}

            <div className="flex items-center justify-between mt-8 gap-3">
              <button onClick={back} data-testid="diagnostic-back-button" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 font-semibold transition-colors">
                <ArrowLeft size={18} /> Retour
              </button>
              <button
                onClick={next}
                data-testid="diagnostic-next-button"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-7 py-3 font-semibold transition-colors shadow-sm"
              >
                {blockIndex + 1 === totalBlocks ? "Voir mon résultat" : "Continuer"} <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {/* CAPTURE */}
        {state === STATES.CAPTURE && (
          <motion.div key="capture" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }} data-testid="diagnostic-capture">
            <div className="text-center mb-7">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-green-50 text-green-600 mb-4">
                <FileText size={26} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">Votre rapport est prêt !</h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Indiquez votre email pour découvrir votre score, votre potentiel et votre plan d'action personnalisé.
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <Label htmlFor="lead-commerce" className="block text-base font-semibold text-slate-900 mb-2">
                  Nom de votre commerce
                </Label>
                <Input
                  id="lead-commerce"
                  data-testid="capture-commerce"
                  value={lead.commerce}
                  onChange={(e) => setLead({ ...lead, commerce: e.target.value })}
                  placeholder="Ex : Boulangerie du Centre"
                />
              </div>
              <div>
                <Label htmlFor="lead-email" className="block text-base font-semibold text-slate-900 mb-2">
                  Votre email <span className="text-green-600">*</span>
                </Label>
                <Input
                  id="lead-email"
                  type="email"
                  data-testid="capture-email"
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  placeholder="vous@exemple.fr"
                />
              </div>
            </div>

            {error && <p className="text-red-600 text-sm mt-4" data-testid="diagnostic-error">{error}</p>}

            <button
              data-testid="capture-submit-button"
              onClick={submitLead}
              className="w-full mt-7 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-7 py-3.5 font-semibold text-lg transition-colors shadow-sm"
            >
              Voir mon rapport <ArrowRight size={20} />
            </button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 mt-4">
              <Lock size={13} /> Vos données restent confidentielles. Aucun spam.
            </p>
            <button onClick={() => setState(STATES.FORM)} data-testid="capture-back-button" className="mx-auto mt-3 flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-colors">
              <ArrowLeft size={16} /> Revenir aux questions
            </button>
          </motion.div>
        )}

        {/* LOADING */}
        {state === STATES.LOADING && (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            <Loader2 size={40} className="animate-spin text-green-600 mx-auto mb-5" />
            <p className="text-slate-600 font-medium">Analyse de votre visibilité en cours...</p>
            <p className="text-slate-400 text-sm mt-1">Nous préparons votre rapport personnalisé.</p>
          </motion.div>
        )}

        {/* RESULT */}
        {state === STATES.RESULT && result && (
          <motion.div key="result" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="diagnostic-result">
            <div className="text-center mb-8">
              <p className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-2">Votre rapport de visibilité</p>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{result.titre}</h3>
              {priority && (
                <span className={`inline-block mt-3 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${priority.cls}`} data-testid="result-priority">
                  {priority.label}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="rounded-xl bg-slate-50 p-5 text-center">
                <p className="text-sm text-slate-500 mb-1">Visibilité actuelle</p>
                <p data-testid="result-score" className="font-heading text-4xl font-bold text-slate-900">{result.score_actuel}%</p>
              </div>
              <div className="rounded-xl bg-green-50 p-5 text-center">
                <p className="text-sm text-green-700 mb-1">Potentiel d'amélioration</p>
                <p data-testid="result-potential" className="font-heading text-4xl font-bold text-green-600">{result.potentiel}%</p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-white p-5 mb-6 flex gap-3">
              <FileText size={20} className="text-green-600 shrink-0 mt-0.5" />
              <p className="text-slate-700 leading-relaxed" data-testid="result-explanation">{result.explication}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
              <div data-testid="result-strengths">
                <p className="flex items-center gap-2 font-heading font-semibold text-slate-900 mb-3"><ShieldCheck size={18} className="text-green-600" /> Points forts</p>
                {result.points_forts.length ? (
                  <ul className="space-y-2">
                    {result.points_forts.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check size={16} className="text-green-600 mt-0.5 shrink-0" /> {p}</li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-slate-400">Tout est à construire — c'est une belle opportunité !</p>}
              </div>
              <div data-testid="result-improvements">
                <p className="flex items-center gap-2 font-heading font-semibold text-slate-900 mb-3"><AlertCircle size={18} className="text-orange-500" /> À améliorer</p>
                {result.points_ameliorer.length ? (
                  <ul className="space-y-2">
                    {result.points_ameliorer.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="h-1.5 w-1.5 rounded-full bg-orange-400 mt-2 shrink-0" /> {p}</li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-slate-400">Rien de critique, félicitations !</p>}
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-5 mb-6" data-testid="result-actions">
              <p className="flex items-center gap-2 font-heading font-semibold text-slate-900 mb-3"><ListChecks size={18} className="text-green-600" /> Vos actions prioritaires</p>
              <ol className="space-y-2">
                {result.actions.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
                    {a}
                  </li>
                ))}
              </ol>
            </div>

            {recommendedPack && (
              <div className="rounded-xl border-2 border-green-600 bg-green-50/50 p-6 mb-6" data-testid="result-pack">
                <p className="flex items-center gap-2 text-sm font-semibold text-green-700 uppercase tracking-wider mb-1"><Target size={15} /> Pack recommandé</p>
                <h4 className="font-heading text-2xl font-bold text-slate-900 mb-2">{recommendedPack.name}</h4>
                {recommendedPack.price && (
                  <p className="mb-2">
                    <span className="font-heading text-2xl font-bold text-green-600">{recommendedPack.price}€</span>
                    {recommendedPack.oldPrice && (
                      <>
                        <span className="text-slate-400 line-through ml-2">{recommendedPack.oldPrice}€</span>
                        <span className="ml-2 text-xs font-bold uppercase tracking-wider text-green-700 bg-green-100 rounded-full px-2 py-0.5">Offre de lancement</span>
                      </>
                    )}
                  </p>
                )}
                <p className="text-slate-600 mb-4">{recommendedPack.description}</p>
                <button
                  data-testid="result-request-pack-button"
                  onClick={() => navigate(`/contact?pack=${recommendedPack.id}`)}
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-3 font-semibold transition-colors"
                >
                  Demander ce pack <ArrowRight size={18} />
                </button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                data-testid="result-restart-button"
                onClick={start}
                className="inline-flex items-center justify-center gap-2 border-2 border-slate-200 hover:bg-slate-50 text-slate-800 rounded-xl px-6 py-3 font-semibold transition-colors"
              >
                <RotateCcw size={18} /> Refaire le diagnostic
              </button>
              <button
                data-testid="result-view-offers-button"
                onClick={() => navigate("/offres")}
                className="inline-flex items-center justify-center gap-2 text-slate-700 hover:text-green-600 rounded-xl px-6 py-3 font-semibold transition-colors"
              >
                Voir toutes les offres
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DiagnosticWizard;
